import { useCallback, useEffect, useState } from "react";
import { UserX, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

interface OptedOutStudent {
  destination: string;
  userName?: string;
  recipientUserId?: string;
  templateName: string;
  errorMessage: string;
  lastAttemptAt: string;
}

/**
 * Shows students AiSensy has rejected a send to with "This Contact is
 * Opted-out". There is no AiSensy API to list or poll opt-out status
 * directly (confirmed — only the campaign-send endpoint is documented, and
 * their own dashboard's internal endpoint for this is locked to browser
 * sessions), so this list is reactive: a student only appears once we've
 * actually tried to message them and it bounced. Someone opted-out that we
 * haven't attempted to re-send to since simply won't show up here.
 *
 * Resubscribing has to happen in AiSensy's own dashboard (Contacts →
 * per-contact "opted out" toggle) — there's nothing to action from here.
 */
export default function OptedOutStudents() {
  const [students, setStudents] = useState<OptedOutStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get<OptedOutStudent[]>(
        "/admin/whatsapp/opted-out-students",
      );
      setStudents(resp || []);
    } catch (e: any) {
      setError(e?.message || "Failed to load opted-out students");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
            <UserX className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Opted-out students
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Students whose last send attempt was rejected as opted-out.
              Reactive only — someone we haven't tried messaging since they
              opted out won't appear here. Resubscribing has to be done in
              AiSensy's own dashboard (Contacts → per-contact toggle); reply
              &quot;START&quot; from the student's number also works.
            </p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="w-full sm:w-auto shrink-0 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={loading ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : students.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-500">
          No known opted-out students from recent send attempts.
        </div>
      ) : (
        <div className="mt-4 border border-gray-200 rounded-md divide-y divide-gray-100 max-h-96 overflow-y-auto">
          {students.map((s) => (
            <div
              key={s.destination}
              className="p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1"
            >
              <div className="min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {s.userName || "Unknown name"}
                </div>
                <div className="text-xs text-gray-500">{s.destination}</div>
              </div>
              <div className="text-xs text-gray-500 sm:text-right">
                <div>Last tried: {s.templateName}</div>
                <div>{new Date(s.lastAttemptAt).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
